// Import statements
import {
  EntityTypes,
  formatCurrency,
  InventoryChange,
  InventoryChangeReason,
  StoreProduct,
} from '@bofrak-backend/shared';
import {
  Alert,
  AlertIcon,
  Box,
  chakra,
  FormControl,
  FormLabel,
  HStack,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { TimeIcon } from '@chakra-ui/icons';
import { InventoryValue } from '../general/InventoryValue';
import { BreakdownText } from '../general/BreakdownText';
import { formatInventoryBreakdown } from '../../reports/utils/format-quantity-sold';
import { useSharedUiTranslation } from '../../i18n';
import { getInventoryReasonLabel } from './inventory-reason-label';

// Custom table header component
const CustomTh = chakra('th', {
  baseStyle: {
    borderBottom: '1px solid',
    borderColor: 'gray.300',
  },
});

interface TableRowProps extends InventoryChange {
  show_product_name?: boolean;
  product?: StoreProduct;
  detailed?: boolean;
  show_sync_time?: boolean;
  order_by_sync_time?: boolean;
}

// TableRow component
const TableRow: React.FC<TableRowProps> = ({
  change_quantity: adjustment,
  created_at,
  cumulative_quantity: after_stock,
  reason,
  cost,
  source_name,
  source_id,
  product_name,
  note,
  show_product_name = true,
  purchase_order_details,
  source_type,
  product,
  detailed = false,
  processed_at,
  show_sync_time = true,
  order_by_sync_time = false,
}) => {
  const { t } = useSharedUiTranslation();
  const { quantity: displayedAdjustment, color: adjustmentColor } =
    useMemo(() => {
      if (reason === InventoryChangeReason.SALE) {
        return {
          quantity: -Math.abs(adjustment),
          color: 'red.500',
        } as const;
      }

      if (reason === InventoryChangeReason.REFUND) {
        return {
          quantity: Math.abs(adjustment),
          color: 'green.500',
        } as const;
      }

      if (adjustment === 0) {
        return {
          quantity: adjustment,
          color: 'gray.500',
        } as const;
      }

      return {
        quantity: adjustment,
        color: adjustment > 0 ? 'green.500' : 'red.500',
      } as const;
    }, [adjustment, reason]);

  const syncLabel = useMemo(() => {
    if (!processed_at) return null;
    const diffMs =
      new Date(processed_at).getTime() - new Date(created_at).getTime();
    if (Math.abs(diffMs) <= 1000) return null;
    const createdDate = moment(created_at).format('DD.MM.YY');
    const processedDate = moment(processed_at).format('DD.MM.YY');
    const time = moment(processed_at).format('HH:mm:ss');
    return createdDate === processedDate
      ? time
      : `${moment(processed_at).format('DD.MM.YY @ HH:mm:ss')}`;
  }, [processed_at, created_at]);

  const cost_difference = displayedAdjustment * cost;

  const adjustmentBreakdown = detailed
    ? formatInventoryBreakdown(displayedAdjustment, product, { showSign: true })
    : null;
  const stockBreakdown = detailed
    ? formatInventoryBreakdown(after_stock, product)
    : null;

  const isOrderedBySync = order_by_sync_time && processed_at;

  return (
    <Tr fontSize={'sm'}>
      {/* Date and User */}
      <Td>
        <Box>
          {show_product_name && (
            <Text fontWeight={'bold'} fontSize={'xs'}>
              {product_name}
            </Text>
          )}
          {/* created_at — always on top */}
          <Text
            fontWeight={
              isOrderedBySync ? 'normal' : show_product_name ? 'normal' : 'bold'
            }
            fontSize={isOrderedBySync ? '2xs' : 'xs'}>
            {moment(created_at).format('DD.MM.YY @ HH:mm:ss')}
          </Text>
          {/* processed_at — always on bottom when applicable */}
          {(isOrderedBySync || (show_sync_time && syncLabel)) && (
            <Text
              p={0}
              m={0}
              fontWeight={isOrderedBySync ? 'bold' : 'bold'}
              fontSize={isOrderedBySync ? 'xs' : '2xs'}
              color="red.500"
              ml={1}>
              <TimeIcon boxSize="10px" mr={1} />{' '}
              {isOrderedBySync
                ? moment(processed_at).format('DD.MM.YY @ HH:mm:ss')
                : syncLabel}
            </Text>
          )}
          <Text fontSize={'xs'}>
            {source_name ?? t('Unknown').toUpperCase()}
          </Text>
          {note && (
            <Text fontSize="2xs" color="gray.500" noOfLines={2}>
              {note}
            </Text>
          )}
        </Box>
      </Td>

      {/* Adjustment, After Stock, Cost Difference */}
      <Td>
        {adjustmentBreakdown?.text || stockBreakdown?.text ? (
          <VStack spacing={1} align="start" py={1}>
            {adjustmentBreakdown?.text ? (
              <BreakdownText
                fontSize="2xs"
                color={adjustmentColor}
                items={adjustmentBreakdown.breakDownItems}
              />
            ) : (
              <InventoryValue
                value={displayedAdjustment}
                showSign
                fontSize="xs"
                textTransform="lowercase"
                color={adjustmentColor}
              />
            )}
            <Box borderTop="1px dashed" borderColor="gray.300" w="full" />
            {stockBreakdown?.text ? (
              <BreakdownText
                fontSize="2xs"
                items={stockBreakdown.breakDownItems}
              />
            ) : (
              <InventoryValue value={after_stock} fontWeight="bold" />
            )}
          </VStack>
        ) : (
          <Box position="relative" minW="100px" h="50px">
            <Box position="absolute" left="30%" top="0">
              <InventoryValue
                value={displayedAdjustment}
                showSign
                fontSize="xs"
                textTransform="lowercase"
                color={adjustmentColor}
              />
            </Box>
            <Box
              position="absolute"
              left="50%"
              top="50%"
              transform="translate(-50%, -50%)">
              <InventoryValue value={after_stock} fontWeight="bold" />
            </Box>
            <Text
              fontSize="xs"
              position="absolute"
              top="70%"
              right="30%"
              bottom="0"
              display={'none'}
              color={
                cost_difference === 0
                  ? 'gray.500'
                  : cost_difference > 0
                    ? 'green.500'
                    : 'red.500'
              }>
              {formatCurrency(cost_difference, false)}
            </Text>
          </Box>
        )}
      </Td>

      {/* Reason */}
      <Td>
        <Text fontWeight={'bold'} textAlign={'center'} fontSize={'xs'}>
          {getInventoryReasonLabel(reason, t, true).toUpperCase()}
        </Text>
        {[
          InventoryChangeReason.SALE,
          InventoryChangeReason.REFUND,
          InventoryChangeReason.PURCHASE_ORDER,
        ].includes(reason) &&
          (purchase_order_details || source_type === EntityTypes.RECEIPT) && (
            <Text
              fontWeight={'bold'}
              color={'blue'}
              textAlign={'center'}
              fontSize={'xx-small'}>
              {purchase_order_details
                ? purchase_order_details.id.toUpperCase()
                : source_id.toUpperCase()}
            </Text>
          )}
      </Td>
    </Tr>
  );
};

// InventoryHistoryTable component
export const InventoryHistoryTable: React.FC<{
  data: InventoryChange[];
  show_product_name?: boolean;
  storeProducts?: StoreProduct[];
}> = ({ data, show_product_name, storeProducts = [] }) => {
  const { t } = useSharedUiTranslation();
  const [detailed, setDetailed] = useState(true);
  const [showSyncTime, setShowSyncTime] = useState(true);
  const [orderBySyncTime, setOrderBySyncTime] = useState(false);

  const sortedData = useMemo(() => {
    if (!orderBySyncTime) return data;
    return [...data].sort((a, b) => {
      const aTime = a.processed_at ?? a.created_at;
      const bTime = b.processed_at ?? b.created_at;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });
  }, [data, orderBySyncTime]);

  const productMap = useMemo(() => {
    const map = new Map<string, StoreProduct>();
    for (const p of storeProducts) {
      map.set(p.id, p);
      for (const child of p.store_product_fractions ?? []) {
        map.set(child.id, p);
      }
    }
    return map;
  }, [storeProducts]);

  const toggleSwitch = (
    <HStack justify="flex-end" px={2} py={1} spacing={4}>
      <FormControl display="flex" alignItems="center" w="auto">
        <FormLabel
          htmlFor="iht-order-sync-toggle"
          mb={0}
          fontSize="xs"
          cursor="pointer">
          {t('Order by sync')}
        </FormLabel>
        <Switch
          id="iht-order-sync-toggle"
          size="sm"
          isChecked={orderBySyncTime}
          onChange={(e) => setOrderBySyncTime(e.target.checked)}
        />
      </FormControl>
      <FormControl display="flex" alignItems="center" w="auto">
        <FormLabel
          htmlFor="iht-sync-toggle"
          mb={0}
          fontSize="xs"
          cursor="pointer">
          {t('Sync time')}
        </FormLabel>
        <Switch
          id="iht-sync-toggle"
          size="sm"
          isChecked={showSyncTime}
          onChange={(e) => setShowSyncTime(e.target.checked)}
        />
      </FormControl>
      {storeProducts.length > 0 && (
        <FormControl display="flex" alignItems="center" w="auto">
          <FormLabel
            htmlFor="iht-detailed-toggle"
            mb={0}
            fontSize="xs"
            cursor="pointer">
            {detailed ? t('Detailed') : t('Simple')}
          </FormLabel>
          <Switch
            id="iht-detailed-toggle"
            size="sm"
            isChecked={detailed}
            onChange={(e) => setDetailed(e.target.checked)}
          />
        </FormControl>
      )}
    </HStack>
  );

  return data.length ? (
    <>
      {toggleSwitch}
      <TableContainer width="full">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr fontSize={'sm'}>
              <CustomTh></CustomTh>
              <CustomTh>
                <Box position="relative" p={2} minW="100px" h="50px">
                  <Text
                    textTransform={'lowercase'}
                    fontSize="xs"
                    position="absolute"
                    left="0"
                    top="0">
                    {t('adj')}
                  </Text>
                  <Text
                    position="absolute"
                    left="50%"
                    top="50%"
                    transform="translate(-50%, -50%)">
                    {t('After Stock')}
                  </Text>
                  <Text
                    textTransform={'lowercase'}
                    fontSize="xs"
                    position="absolute"
                    display={'none'}
                    right="0"
                    bottom="0">
                    {t('cost diff')}
                  </Text>
                </Box>
              </CustomTh>
              <CustomTh>{t('Reason')}</CustomTh>
            </Tr>
          </Thead>
          <Tbody>
            {sortedData.map((row) => (
              <TableRow
                key={row.id}
                {...row}
                show_product_name={show_product_name}
                product={productMap.get(row.product_id)}
                detailed={detailed}
                show_sync_time={showSyncTime}
                order_by_sync_time={orderBySyncTime}
              />
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  ) : (
    <Alert textTransform={'capitalize'} width={'auto'} status="info">
      <AlertIcon />
      {t('No History Found')}
    </Alert>
  );
};
